import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { EGovAutomationDashboardClient, EGovServiceRequestDto } from 'src/app/web-api-client';
import { ServiceRequestStatusHelper } from 'src/app/shared/enum/serviceRequestStatus.enum';

@Component({
  selector: 'app-egov-requests-table',
  templateUrl: './egov-requests-table.component.html',
  styleUrls: ['./egov-requests-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EGovRequestsTableComponent implements OnInit, OnChanges {
  @Input() isRefreshed: boolean = false;
  @Input() startDate: string;
  @Input() endDate: string;

  public isLoading: boolean = true;
  public serviceRequests: EGovServiceRequestDto[] = [];

  constructor(
    private egovDashboardClient: EGovAutomationDashboardClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isRefreshed && changes.isRefreshed.currentValue !== changes.isRefreshed.previousValue) {
      this.loadData();
    }
    if ((changes['startDate'] || changes['endDate']) && !changes['startDate']?.firstChange && !changes['endDate']?.firstChange) {
      this.loadData();
    }
  }

  private loadData(): void {
    this.isLoading = true;
    this.cdr.detectChanges();

    // Convert string dates to Date objects for API call
    const startDateTime = this.startDate ? new Date(this.startDate) : undefined;
    const endDateTime = this.endDate ? new Date(this.endDate) : undefined;

    this.egovDashboardClient.getServiceRequests(startDateTime, endDateTime).subscribe({
      next: (data) => {
        this.serviceRequests = data;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading service requests:', error);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  public getRequestorName(serviceRequest: EGovServiceRequestDto): string {
    return `${serviceRequest.firstName} ${serviceRequest.fammily}`;
  }

  public getServiceName(serviceRequest: EGovServiceRequestDto): string {
    return serviceRequest.serviceName || 'N/A';
  }

  public getServiceTitle(serviceRequest: EGovServiceRequestDto): string {
    return serviceRequest.serviceTitle || 'N/A';
  }

  public getURI(serviceRequest: EGovServiceRequestDto): string {
    return serviceRequest.serviceURI || 'N/A';
  }

  public getProviderName(serviceRequest: EGovServiceRequestDto): string {
    return serviceRequest.serviceProviderName || 'N/A';
  }

  public getTotalTax(serviceRequest: EGovServiceRequestDto): string {
    return `${(serviceRequest.totalTax || 0).toFixed(2)} лв.`;
  }

  public getPaymentStatus(serviceRequest: EGovServiceRequestDto): 'paid' | 'partial' | 'unpaid' | 'none' {
    const total = serviceRequest.totalTax || 0;
    const paid = serviceRequest.paidTax || 0;

    if (total === 0) return 'none';
    if (paid >= total) return 'paid';
    if (paid > 0) return 'partial';
    return 'unpaid';
  }

  public getPaymentBadgeClass(serviceRequest: EGovServiceRequestDto): string {
    const status = this.getPaymentStatus(serviceRequest);
    switch (status) {
      case 'paid': return 'badge bg-success';
      case 'partial': return 'badge bg-warning text-dark';
      case 'unpaid': return 'badge bg-danger';
      default: return '';
    }
  }

  public getPaymentBadgeText(serviceRequest: EGovServiceRequestDto): string {
    const status = this.getPaymentStatus(serviceRequest);
    switch (status) {
      case 'paid': return 'Платена';
      case 'partial': return 'Частично';
      case 'unpaid': return 'Неплатена';
      default: return '';
    }
  }

  public getStatus(serviceRequest: EGovServiceRequestDto): string {
    return ServiceRequestStatusHelper.GetCyrilicName(serviceRequest.status);
  }

  public getDate(serviceRequest: EGovServiceRequestDto): string {
    const date = new Date(serviceRequest.dateReg);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  public getStatusBadgeClass(status: number): string {
    switch (status) {
      case 4: // AutomatedNew
        return 'badge-info';
      case 5: // AutomatedPaid
        return 'badge-success';
      case 6: // AutomatedPaymentExpired
        return 'badge-warning';
      case 7: // AutomatedPaymentSuspended
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  public getRNU(serviceRequest: EGovServiceRequestDto): string {
    return serviceRequest.rnu || 'N/A';
  }

  public getDeliverServiceName(serviceRequest: EGovServiceRequestDto): string {
    return serviceRequest.deliverServiceName || 'N/A';
  }

  public getServiceTermType(serviceRequest: EGovServiceRequestDto): string {
    switch (serviceRequest.serviceTermType) {
      case 1: return 'Експресна';
      case 2: return 'Бърза';
      case 3: return 'Обикновена';
      default: return 'Неизвестен';
    }
  }

  public getSample(serviceRequest: EGovServiceRequestDto): string {
    return serviceRequest.sample || 'N/A';
  }
}

